import { useState, useRef, useCallback, Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  ChevronDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  TrashIcon,
  EyeSlashIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import type { ColumnType } from '@wmp/shared/constants';

interface ColumnHeaderProps {
  id: string;
  name: string;
  type: ColumnType;
  width: number;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: (direction: 'asc' | 'desc' | null) => void;
  onResize?: (newWidth: number) => void;
  onRename?: (newName: string) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onHide?: () => void;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
}

// Column type icons/labels
const COLUMN_TYPE_LABELS: Record<ColumnType, string> = {
  status: 'Status',
  people: 'People',
  date: 'Date',
  timeline: 'Timeline',
  numbers: 'Numbers',
  text: 'Text',
  link: 'Link',
  checkbox: 'Checkbox',
  dropdown: 'Dropdown',
  files: 'Files',
  priority: 'Priority',
  tags: 'Tags',
  formula: 'Formula',
};

export function ColumnHeader({
  id: _id,
  name,
  type,
  width,
  sortDirection,
  onSort,
  onResize,
  onRename,
  onDelete,
  onDuplicate,
  onHide,
  onMoveLeft,
  onMoveRight,
}: ColumnHeaderProps) {
  const [isResizing, setIsResizing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(name);
  const startXRef = useRef(0);
  const startWidthRef = useRef(width);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startXRef.current;
      const newWidth = Math.max(80, Math.min(500, startWidthRef.current + delta));
      onResize?.(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [width, onResize]);

  const handleSort = () => {
    if (sortDirection === 'asc') {
      onSort?.('desc');
    } else if (sortDirection === 'desc') {
      onSort?.(null);
    } else {
      onSort?.('asc');
    }
  };

  const handleNameSubmit = () => {
    setIsEditing(false);
    if (editedName !== name && editedName.trim()) {
      onRename?.(editedName.trim());
    } else {
      setEditedName(name);
    }
  };

  return (
    <div
      className="relative flex items-center h-10 px-3 bg-white/5 border-b border-r border-white/10 group"
      style={{ width, minWidth: width }}
    >
      {/* Column content */}
      <div className="flex items-center gap-2 flex-1 overflow-hidden">
        {/* Column name */}
        {isEditing ? (
          <input
            type="text"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            onBlur={handleNameSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleNameSubmit();
              if (e.key === 'Escape') {
                setIsEditing(false);
                setEditedName(name);
              }
            }}
            className="flex-1 text-sm font-medium bg-transparent border-b border-primary-500 outline-none text-white"
            autoFocus
          />
        ) : (
          <button
            onClick={handleSort}
            className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-gray-200 truncate"
          >
            <span className="truncate">{name}</span>
            {sortDirection === 'asc' && <ArrowUpIcon className="h-3 w-3 text-primary-400" />}
            {sortDirection === 'desc' && <ArrowDownIcon className="h-3 w-3 text-primary-400" />}
          </button>
        )}
      </div>

      {/* Column menu */}
      <ColumnOptionsMenu
        type={type}
        onRename={() => setIsEditing(true)}
        onDuplicate={onDuplicate}
        onHide={onHide}
        onMoveLeft={onMoveLeft}
        onMoveRight={onMoveRight}
        onDelete={onDelete}
      />

      {/* Resize handle */}
      <div
        className={`
          absolute right-0 top-0 bottom-0 w-1 cursor-col-resize
          hover:bg-primary-500 transition-colors
          ${isResizing ? 'bg-primary-500' : ''}
        `}
        onMouseDown={handleMouseDown}
      />
    </div>
  );
}

/**
 * Column Options Dropdown Menu
 */
function ColumnOptionsMenu({
  type,
  onRename,
  onDuplicate,
  onHide,
  onMoveLeft,
  onMoveRight,
  onDelete,
}: {
  type: ColumnType;
  onRename?: () => void;
  onDuplicate?: () => void;
  onHide?: () => void;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
  onDelete?: () => void;
}) {
  return (
    <Menu as="div" className="relative">
      <Menu.Button className="p-1 rounded hover:bg-white/10 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
        <ChevronDownIcon className="h-4 w-4" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-52 dropdown-glass">
          {/* Column type indicator */}
          <div className="px-4 py-2 text-xs text-gray-500 border-b border-white/10">
            Column type: {COLUMN_TYPE_LABELS[type]}
          </div>

          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onRename}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <PencilIcon className="h-4 w-4 text-gray-500" />
                Rename column
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4 text-gray-500" />
                Column settings
              </button>
            )}
          </Menu.Item>

          <div className="my-1 border-t border-white/10" />

          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onMoveLeft}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <ArrowLeftIcon className="h-4 w-4 text-gray-500" />
                Move left
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onMoveRight}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <ArrowRightIcon className="h-4 w-4 text-gray-500" />
                Move right
              </button>
            )}
          </Menu.Item>

          <div className="my-1 border-t border-white/10" />

          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onDuplicate}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <DocumentDuplicateIcon className="h-4 w-4 text-gray-500" />
                Duplicate column
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onHide}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <EyeSlashIcon className="h-4 w-4 text-gray-500" />
                Hide column
              </button>
            )}
          </Menu.Item>

          <div className="my-1 border-t border-white/10" />

          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onDelete}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-red-500/10' : ''} text-red-400`}
              >
                <TrashIcon className="h-4 w-4" />
                Delete column
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
